/**
 * Broadcast filter types for targeting notification recipients.
 * Re-exports from generated API types for convenience.
 */

import {
  BroadcastFilterDetailResponse,
  BroadcastFilterDetailResponseFilterType,
  CreateBroadcastFilterRequestFilterType,
} from '@/api/auth/missionImpactAuthAPI.schemas';

export type BroadcastFilterType = BroadcastFilterDetailResponseFilterType;
export const BroadcastFilterType = BroadcastFilterDetailResponseFilterType;

export type BroadcastFilter = BroadcastFilterDetailResponse;

/**
 * Human-readable labels for filter types
 */
export const FILTER_TYPE_LABELS: Record<BroadcastFilterType, string> = {
  All: 'All Recipients',
  Ids: 'Specific IDs',
  ProductRegistration: 'Product Registration',
  PaidSubscription: 'Paid Subscription',
  BillingCountry: 'Billing Country',
};

/**
 * Descriptions for filter types
 */
export const FILTER_TYPE_DESCRIPTIONS: Record<BroadcastFilterType, string> = {
  All: 'Target all users or organizations',
  Ids: 'Target specific user or organization IDs',
  ProductRegistration: 'Target users registered for specific products',
  PaidSubscription: 'Target users with active paid subscriptions',
  BillingCountry: 'Target users by their billing country',
};

/**
 * Converts API filter response to our typed filter format.
 * The old API returns filters wrapped in { filter: {...} } objects with polymorphic types.
 * The new API returns BroadcastFilterDetailResponse directly.
 */
export function parseFiltersFromApi(
  apiFilters?: Array<{ filter?: unknown }>
): BroadcastFilter[] {
  if (!apiFilters) return [];

  const result: BroadcastFilter[] = [];
  for (const wrapper of apiFilters) {
    const f = wrapper.filter as Record<string, unknown> | undefined;
    if (!f || typeof f.filterType !== 'string') continue;

    // Map from polymorphic response to BroadcastFilterDetailResponse format
    result.push({
      id: f.id as string | undefined,
      broadcastId: f.notificationBroadcastId as string | undefined,
      filterType: f.filterType as BroadcastFilterType,
      ids: f.ids as string[] | undefined,
      productIds: f.productIds as string[] | undefined,
      countryCodes: f.countryCodes as string[] | undefined,
    });
  }
  return result;
}

/**
 * Maps our filter type to the API request filter type
 */
export function toApiFilterType(filterType: BroadcastFilterType): CreateBroadcastFilterRequestFilterType {
  return filterType as CreateBroadcastFilterRequestFilterType;
}
